/**
 * Network simulation: artificial latency / jitter / loss for a one-way link.
 *
 * Deterministic when given a seed (xorshift32), injectable clock/timers so it
 * runs under tests' VirtualClock, and usable on a live server via the
 * NETSIM_* env vars (see wrapSocket below).
 *
 * `ordered: true` (default) models a TCP/WebSocket stream: a "lost" packet is
 * dropped (models volatile emits), and delivery order is preserved so a
 * jitter spike delays everything behind it (head-of-line blocking) — which is
 * exactly the failure mode the design doc worried about, so tests can provoke it.
 */
export interface NetSimOptions {
  /** Base one-way latency, ms. */
  latency?: number
  /** Uniform jitter, ms — delay = latency + U(-jitter, +jitter). */
  jitter?: number
  /** Drop probability 0..1. */
  loss?: number
  ordered?: boolean
  seed?: number
  now?: () => number
  setTimer?: (fn: () => void, ms: number) => unknown
}

export class NetLink {
  readonly latency: number
  readonly jitter: number
  readonly loss: number
  readonly ordered: boolean
  private rng: number
  private readonly now: () => number
  private readonly setTimer: (fn: () => void, ms: number) => unknown
  private lastDeliverAt = -Infinity
  sent = 0
  dropped = 0
  delivered = 0

  constructor(opts: NetSimOptions = {}) {
    this.latency = opts.latency ?? 0
    this.jitter = opts.jitter ?? 0
    this.loss = opts.loss ?? 0
    this.ordered = opts.ordered ?? true
    this.rng = (opts.seed ?? 0x1234abcd) >>> 0 || 1
    this.now = opts.now ?? (() => performance.now())
    this.setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms))
  }

  private rand(): number {
    let x = this.rng
    x ^= x << 13; x >>>= 0
    x ^= x >>> 17
    x ^= x << 5; x >>>= 0
    this.rng = x
    return x / 0x100000000
  }

  /** Schedule `deliver` across the link. Returns false if dropped. */
  send(deliver: () => void): boolean {
    this.sent++
    if (this.loss > 0 && this.rand() < this.loss) { this.dropped++; return false }
    const jitter = this.jitter > 0 ? (this.rand() * 2 - 1) * this.jitter : 0
    let at = this.now() + Math.max(0, this.latency + jitter)
    if (this.ordered && at < this.lastDeliverAt) at = this.lastDeliverAt
    this.lastDeliverAt = at
    this.setTimer(() => { this.delivered++; deliver() }, at - this.now())
    return true
  }
}

/** A bidirectional pair of links (client→server, server→client). */
export function makeDuplex(opts: NetSimOptions = {}): { up: NetLink; down: NetLink } {
  const seed = opts.seed ?? 0x1234abcd
  return { up: new NetLink({ ...opts, seed }), down: new NetLink({ ...opts, seed: (seed * 31 + 7) >>> 0 }) }
}

/** Minimal shape shared by socket.io server Socket and client Socket. */
interface EmitLike {
  emit: (...args: any[]) => any
  use?: (fn: (packet: any[], next: (err?: Error) => void) => void) => void
  onAny?: (fn: (...args: any[]) => void) => void
  onAnyOutgoing?: (fn: (...args: any[]) => void) => void
}

/** Monkey-patch a socket.io socket so outgoing emits cross `down` and (server-
 *  side only, via socket.use) incoming packets cross `up`. Dev tooling — the
 *  volatile flag is lost on delayed emits, which is fine for a simulator. */
export function wrapSocket(socket: EmitLike, links: { up?: NetLink; down?: NetLink }): void {
  if (links.down) {
    const origEmit = socket.emit.bind(socket)
    socket.emit = (...args: any[]) => {
      links.down!.send(() => origEmit(...args))
      return true
    }
  }
  if (links.up && socket.use) {
    socket.use((_packet, next) => {
      if (!links.up!.send(() => next())) {
        // Dropped: never call next(). socket.io just discards the packet.
      }
    })
  }
}
